import React from 'react';
import { Info } from 'lucide-react';

interface ParameterSliderProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  description?: string;
  recommendedMin?: number;
  recommendedMax?: number;
  unit?: string;
  disabled?: boolean;
  showRecommendedRange?: boolean;
}

export const ParameterSlider: React.FC<ParameterSliderProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  step,
  description,
  recommendedMin,
  recommendedMax,
  unit = '',
  disabled = false,
  showRecommendedRange = true
}) => {
  const isInRecommendedRange = recommendedMin !== undefined && recommendedMax !== undefined
    ? value >= recommendedMin && value <= recommendedMax
    : true;

  const percentage = ((value - min) / (max - min)) * 100;

  const getRecommendedRangePercentage = () => {
    if (recommendedMin === undefined || recommendedMax === undefined) return null;

    const startPercent = ((recommendedMin - min) / (max - min)) * 100;
    const endPercent = ((recommendedMax - min) / (max - min)) * 100;

    return { start: startPercent, width: endPercent - startPercent };
  };

  const recommendedRange = getRecommendedRangePercentage();

  const getValueColor = () => {
    if (!isInRecommendedRange) return 'text-yellow-600';
    return 'text-gray-900';
  };

  const getTrackColor = () => {
    if (!isInRecommendedRange) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">
            {label}
          </label>
          {description && (
            <div className="group relative">
              <Info className="w-4 h-4 text-gray-400 cursor-help" />
              <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg">
                {description}
              </div>
            </div>
          )}
        </div>
        <div className={`text-sm font-semibold ${getValueColor()}`}>
          {value}{unit}
        </div>
      </div>

      <div className="relative">
        {/* Track */}
        <div className="h-2 bg-gray-200 rounded-full relative overflow-hidden">
          {/* Recommended range indicator */}
          {showRecommendedRange && recommendedRange && (
            <div
              className="absolute h-full bg-green-100 opacity-50"
              style={{
                left: `${recommendedRange.start}%`,
                width: `${recommendedRange.width}%`
              }}
            />
          )}

          {/* Current value indicator */}
          <div
            className={`absolute h-full ${getTrackColor()} transition-all duration-200`}
            style={{ width: `${percentage}%` }}
          />
        </div>

        {/* Slider input */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          disabled={disabled}
          className="absolute top-0 left-0 w-full h-2 opacity-0 cursor-pointer disabled:cursor-not-allowed"
        />
      </div>

      {/* Range labels and recommended range display */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{min}{unit}</span>
        {showRecommendedRange && recommendedMin !== undefined && recommendedMax !== undefined && (
          <span className={`text-xs ${isInRecommendedRange ? 'text-green-600' : 'text-yellow-600'}`}>
            {isInRecommendedRange ? '✓ ' : '⚠ '}
            Recommended: {recommendedMin}{unit} - {recommendedMax}{unit}
          </span>
        )}
        <span>{max}{unit}</span>
      </div>

      {/* Warning if outside recommended range */}
      {!isInRecommendedRange && (
        <div className="text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
          Current value is outside the recommended range. This may affect output quality or cost.
        </div>
      )}
    </div>
  );
};

export default ParameterSlider;
